// src/pages/inventory/InventoryReports.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    TextField,
    MenuItem,
    Paper,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Divider
} from '@mui/material';
import {
    Assessment as AssessmentIcon,
    Download as DownloadIcon,
    Print as PrintIcon,
    BarChart as BarChartIcon,
    PieChart as PieChartIcon,
    TrendingUp as TrendingUpIcon,
    FilterList as FilterIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

// Función helper para conversión segura de números
const safeNumber = (value, defaultValue = 0) => {
    const num = parseFloat(value);
    return isNaN(num) ? defaultValue : num;
};

const COLUMN_TRANSLATIONS = {
    category_name: 'Categoría',
    inventory_value: 'Valor de Inventario',
    total_quantity: 'Cantidad Total',
    name: 'Nombre',
    value: 'Valor',
    date: 'Fecha',
    inbound: 'Entradas',
    outbound: 'Salidas',
    item_name: 'Nombre del Producto',
    model: 'Modelo',
    brand: 'Marca',
    sku: 'SKU',
    min_stock: 'Stock Mínimo',
    quantity: 'Cantidad',
    warehouse_name: 'Almacén'
};

const InventoryReports = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reportType, setReportType] = useState('value');
    const [categories, setCategories] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [filterCategory, setFilterCategory] = useState('');
    const [filterWarehouse, setFilterWarehouse] = useState('');
    const [dateRange, setDateRange] = useState({
        startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#4CAF50', '#F44336', '#9C27B0', '#673AB7', '#3F51B5'];

    useEffect(() => {
        fetchFilters();
    }, []);

    useEffect(() => {
        if (reportType) {
            generateReport();
        }
    }, [reportType, filterCategory, filterWarehouse, dateRange]);

    const fetchFilters = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            if (!token) {
                throw new Error('Token de autenticación no encontrado');
            }

            // Fetch categories
            const categoriesResponse = await fetch('/api/inventory/categories', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!categoriesResponse.ok) {
                throw new Error(`Error al obtener categorías: ${categoriesResponse.status}`);
            }

            const categoriesData = await categoriesResponse.json();
            setCategories(Array.isArray(categoriesData) ? categoriesData : []);

            // Fetch warehouses
            const warehousesResponse = await fetch('/api/inventory/warehouses', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!warehousesResponse.ok) {
                throw new Error(`Error al obtener almacenes: ${warehousesResponse.status}`);
            }

            const warehousesData = await warehousesResponse.json();
            setWarehouses(Array.isArray(warehousesData) ? warehousesData : []);

            setLoading(false);
        } catch (error) {
            console.error('Error fetching filters:', error);
            setError(error.message || 'Error al cargar filtros');
            setLoading(false);
        }
    };

    const generateReport = async () => {
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem('token');

            if (!token) {
                throw new Error('Token de autenticación no encontrado');
            }

            const queryParams = new URLSearchParams();
            if (filterCategory) queryParams.append('category_id', filterCategory);
            if (filterWarehouse) queryParams.append('warehouse_id', filterWarehouse);
            queryParams.append('start_date', dateRange.startDate);
            queryParams.append('end_date', dateRange.endDate);

            const response = await fetch(`/api/inventory/reports/${reportType}?${queryParams.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            setReportData(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error generating report:', error);
            setError(error.message || 'Error al generar reporte');
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setDateRange(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleExportCSV = () => {
        if (!reportData || reportData.length === 0) return;

        // Extract headers based on first data object
        const headers = Object.keys(reportData[0]);

        // Create CSV content
        let csvContent = headers.join(',') + '\n';

        reportData.forEach(item => {
            const row = headers.map(header => {
                // Handle values that might contain commas or quotes
                const value = item[header] != null ? item[header].toString() : '';
                if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                    return `"${value.replace(/"/g, '""')}"`;
                }
                return value;
            }).join(',');
            csvContent += row + '\n';
        });

        // Create and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `inventario_${reportType}_${new Date().toISOString().substring(0, 10)}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handlePrint = () => {
        window.print();
    };

    const renderReportChart = () => {
        if (!reportData || reportData.length === 0) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <Typography color="text.secondary">No hay datos para mostrar</Typography>
                </Box>
            );
        }

        switch (reportType) {
            case 'value':
                return (
                    <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={reportData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="category_name" />
                            <YAxis />
                            <Tooltip formatter={(value) => `S/. ${safeNumber(value).toFixed(2)}`} />
                            <Legend />
                            <Bar dataKey="inventory_value" name="Valor de Inventario" fill="#0088FE" />
                        </BarChart>
                    </ResponsiveContainer>
                );
            case 'distribution':
                return (
                    <ResponsiveContainer width="100%" height={400}>
                        <PieChart>
                            <Pie
                                data={reportData}
                                cx="50%"
                                cy="50%"
                                labelLine={true}
                                outerRadius={150}
                                fill="#8884d8"
                                dataKey="value"
                                nameKey="name"
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                            >
                                {reportData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value, name, props) => [`${value} unidades`, props.payload.name]} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                );
            case 'movements':
                return (
                    <ResponsiveContainer width="100%" height={400}>
                        <LineChart data={reportData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="inbound" name="Entradas" stroke="#4CAF50" strokeWidth={2} />
                            <Line type="monotone" dataKey="outbound" name="Salidas" stroke="#F44336" strokeWidth={2} />
                        </LineChart>
                    </ResponsiveContainer>
                );
            case 'low_stock':
                return (
                    <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={reportData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis dataKey="item_name" type="category" width={150} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="quantity" name="Stock Actual" fill="#0088FE" />
                            <Bar dataKey="min_stock" name="Stock Mínimo" fill="#FF8042" />
                        </BarChart>
                    </ResponsiveContainer>
                );
            default:
                return null;
        }
    };

    const renderReportTable = () => {
        if (!reportData || reportData.length === 0) {
            return null;
        }

        const headers = Object.keys(reportData[0]);

        return (
            <TableContainer component={Paper} sx={{ mt: 3 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {headers.map((header) => (
                                <TableCell key={header}>
                                    {COLUMN_TRANSLATIONS[header] ||
                                        header
                                            .replace(/_/g, ' ')
                                            .split(' ')
                                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                            .join(' ')}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {reportData.map((row, index) => (
                            <TableRow key={index}>
                                {headers.map((header) => (
                                    <TableCell key={header}>
                                        {header.includes('value') || header.includes('price') || header.includes('amount')
                                            ? `S/. ${safeNumber(row[header]).toFixed(2)}`
                                            : row[header]}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 3 }}>
                <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Reportes de Inventario
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Tipo de Reporte
                            </Typography>
                            <TextField
                                select
                                fullWidth
                                value={reportType}
                                onChange={(e) => setReportType(e.target.value)}
                                margin="normal"
                            >
                                <MenuItem value="value">
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <BarChartIcon sx={{ mr: 1 }} />
                                        Valor de Inventario
                                    </Box>
                                </MenuItem>
                                <MenuItem value="distribution">
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <PieChartIcon sx={{ mr: 1 }} />
                                        Distribución de Inventario
                                    </Box>
                                </MenuItem>
                                <MenuItem value="movements">
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <TrendingUpIcon sx={{ mr: 1 }} />
                                        Movimientos de Inventario
                                    </Box>
                                </MenuItem>
                                <MenuItem value="low_stock">
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <FilterIcon sx={{ mr: 1 }} />
                                        Productos con Stock Bajo
                                    </Box>
                                </MenuItem>
                            </TextField>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="h6" gutterBottom>
                                Filtros
                            </Typography>

                            <TextField
                                select
                                fullWidth
                                label="Categoría"
                                value={filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                                margin="normal"
                            >
                                <MenuItem value="">Todas</MenuItem>
                                {categories.map((category) => (
                                    <MenuItem key={category.id} value={category.id}>
                                        {category.name}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                select
                                fullWidth
                                label="Almacén"
                                value={filterWarehouse}
                                onChange={(e) => setFilterWarehouse(e.target.value)}
                                margin="normal"
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {warehouses.map((warehouse) => (
                                    <MenuItem key={warehouse.id} value={warehouse.id}>
                                        {warehouse.name}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                fullWidth
                                label="Fecha Inicio"
                                type="date"
                                name="startDate"
                                value={dateRange.startDate}
                                onChange={handleDateChange}
                                margin="normal"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />

                            <TextField
                                fullWidth
                                label="Fecha Fin"
                                type="date"
                                name="endDate"
                                value={dateRange.endDate}
                                onChange={handleDateChange}
                                margin="normal"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="h6" gutterBottom>
                                Acciones
                            </Typography>

                            <Button
                                fullWidth
                                variant="outlined"
                                color="primary"
                                startIcon={<DownloadIcon />}
                                onClick={handleExportCSV}
                                disabled={!reportData.length}
                                sx={{ mb: 1 }}
                            >
                                Exportar CSV
                            </Button>

                            <Button
                                fullWidth
                                variant="outlined"
                                color="secondary"
                                startIcon={<PrintIcon />}
                                onClick={handlePrint}
                                disabled={!reportData.length}
                            >
                                Imprimir
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={9}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h5" gutterBottom>
                            {reportType === 'value' && 'Reporte de Valor de Inventario'}
                            {reportType === 'distribution' && 'Reporte de Distribución de Inventario'}
                            {reportType === 'movements' && 'Reporte de Movimientos de Inventario'}
                            {reportType === 'low_stock' && 'Reporte de Productos con Stock Bajo'}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Período: {format(new Date(dateRange.startDate), 'dd/MM/yyyy')} - {format(new Date(dateRange.endDate), 'dd/MM/yyyy')}
                        </Typography>

                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                {renderReportChart()}
                                {renderReportTable()}
                            </>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default InventoryReports;